import { has, isEqual } from 'lodash';

import type { ScreenerReqFilters } from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isSegmentedFilter,
  type ListFilter,
  type PrimarySelection,
  type RangeFilter,
  type SingleValueFilter,
  type StaticListFilter,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { createSegmentedFilter } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import {
  activateSelection,
  removeSelection,
  updatePrimarySelections,
} from '../utils/primaryFilterUtils';
import { processFilterDependencies } from './filterDependencies';
import {
  addNewFilterGroup,
  applyFilterAction,
  type ConsistencyKey,
  type FilterConsistencyState,
  handleReplaceFilters,
} from './filterGroupConsistency';
import { updatePrimaryFilterState } from './filterPageConsistency';
import { getFilterDefinition } from './filterRegistry';
import {
  type MultiFilterAction,
  multiFilterReducer,
} from './multiFilterReducer';

export type FilterAction =
  | MultiFilterAction
  | { type: 'SET_FILTER'; filter: Filter; groupKeyOverride?: string }
  | {
      type: 'UPDATE_FILTER';
      filter: Partial<Filter> & { id: FilterId };
      groupKeyOverride?: string;
    }
  | { type: 'REMOVE_FILTER'; filterId: FilterId }
  | {
      type: 'REPLACE_FILTERS';
      payload: {
        filterMap: FilterSelectionsMap;
        pageKey: string;
        defaultActiveCount?: number;
        limit?: number;
        /**
         * Apply filter map verbatim, without reconciling the page selections.
         */
        skipReconciliation?: boolean;
      };
    }
  | {
      type: 'ADD_NEW_FILTER_GROUP';
      payload: {
        newFilterKey: ConsistencyKey;
        newFilterMap: FilterSelectionsMap;
      };
    }
  | {
      type: 'UPDATE_PRIMARY_SELECTIONS';
      payload: {
        filterId: FilterId;
        newSelections: TreeSelection[];
        pageKey: string;
        limit: number;
        groupKeyOverride?: string;
      };
    }
  | {
      type: 'TOGGLE_PRIMARY_SELECTION_ACTIVE';
      payload: {
        filterId: FilterId;
        selectionId: string;
        pageKey: string;
        limit: number;
      };
    }
  | {
      type: 'REMOVE_PRIMARY_SELECTION';
      payload: { filterId: FilterId; selectionId: string; pageKey: string };
    }
  | {
      type: 'REORDER_PRIMARY_SELECTIONS';
      payload: {
        filterId: FilterId;
        newPrimarySelections: PrimarySelection[];
        pageKey: string;
      };
    }
  | {
      type: 'UPDATE_SEGMENT_IN_FILTER';
      payload: {
        filterId: FilterId;
        segmentKey: string;
        segmentFilter: Filter;
      };
    }
  | {
      type: 'REMOVE_SEGMENT_FROM_FILTER';
      payload: {
        filterId: FilterId;
        segmentKey: string;
      };
    }
  | {
      type: 'UPDATE_SEGMENTED_FILTER';
      payload: {
        filterId: FilterId;
        wrappedFilter: Filter;
        segmentations?: Partial<Record<keyof ScreenerReqFilters, Filter>>;
      };
    };

/**
 * Manages all filter state transitions, including setting, updating, and synchronizing filters.
 */
export const filterReducer = (
  state: FilterConsistencyState,
  action: FilterAction
): FilterConsistencyState => {
  switch (action.type) {
    case 'UPDATE_MULTI_FILTER_INSTANCE':
    case 'ADD_MULTI_FILTER_INSTANCE':
    case 'REMOVE_MULTI_FILTER_INSTANCE': {
      return multiFilterReducer(state, action);
    }

    case 'SET_FILTER':
    case 'UPDATE_FILTER': {
      // ROLE and SKILL taxonomy updates will either clear dependent filters or update defaults.
      //
      // Compare by taxonomy *identity* (selection payload), not by full object
      // equality including `version`. Callers like
      // `preference-filter-adapters.ts` dispatch updates without a `version`
      // field (user preferences don't carry taxonomy versions), which would
      // look like a value change compared to the filter-state value that has
      // `version` attached from the initial filter construction. That phantom
      // "change" would cascade through `processFilterDependencies` and wipe
      // downstream ROLE selections — e.g. on a geography page after an AI
      // apply dispatched ROLE_TAXONOMY `{default: true, version: '3.1.10'}`
      // and a pref sync immediately dispatched `{default: true}` (no version),
      // the ROLE filter (AI-applied "data scientists") would get cleared by
      // `filterDependencies.ts`' ROLE_TAXONOMY → ROLE dependency even though
      // the semantic taxonomy didn't change.
      const stripTaxonomyVersion = (v: unknown) => {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const { version: _dropVersion, ...rest } = v as Record<
            string,
            unknown
          >;
          return rest;
        }
        return v;
      };

      let roleTaxonomyValueChanged = true;
      let skillTaxonomyValueChanged = true;
      if (
        action.type === 'UPDATE_FILTER' &&
        action.filter.id === FilterId.ROLE_TAXONOMY
      ) {
        const activeMap = state.activeKey
          ? state.groupMap.get(state.activeKey)
          : undefined;
        const existing = activeMap?.get(FilterId.ROLE_TAXONOMY);
        // biome-ignore lint/suspicious/noPrototypeBuiltins: migrated from develop
        const incomingHasValue = Object.prototype.hasOwnProperty.call(
          action.filter,
          'value'
        );
        if (
          existing &&
          existing.type === FilterType.VALUE &&
          incomingHasValue
        ) {
          const existingValue = (existing as SingleValueFilter<unknown>).value;
          const incomingValue = (
            action.filter as Partial<SingleValueFilter<unknown>>
          ).value;
          roleTaxonomyValueChanged = !isEqual(
            stripTaxonomyVersion(existingValue),
            stripTaxonomyVersion(incomingValue)
          );
        }
      }

      if (
        action.type === 'UPDATE_FILTER' &&
        action.filter.id === FilterId.SKILL_TAXONOMY
      ) {
        const activeMap = state.activeKey
          ? state.groupMap.get(state.activeKey)
          : undefined;
        const existing = activeMap?.get(FilterId.SKILL_TAXONOMY);
        const incomingHasValue = has(action.filter, 'value');
        if (
          existing &&
          existing.type === FilterType.VALUE &&
          incomingHasValue
        ) {
          const existingValue = (existing as SingleValueFilter<unknown>).value;
          const incomingValue = (
            action.filter as Partial<SingleValueFilter<unknown>>
          ).value;

          skillTaxonomyValueChanged = !isEqual(
            stripTaxonomyVersion(existingValue),
            stripTaxonomyVersion(incomingValue)
          );
        }
      }

      // Segmentable filters must always be created as SegmentedFilter directly
      // If a non-segmented segmentable filter is passed to SET_FILTER, it's an error
      if (
        action.type === 'SET_FILTER' &&
        canFilterBeSegmented(action.filter) &&
        !isSegmentedFilter(action.filter)
      ) {
        console.error(
          `Attempted to SET_FILTER with a non-segmented segmentable filter (${action.filter.id}). Segmentable filters must be created as SegmentedFilter.`
        );
        return state;
      }

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        action.filter.id,
        action.filter,
        true,
        action?.groupKeyOverride
      );

      if (updatedFilter) {
        // only change dependent filters when taxonomy value changes
        if (
          updatedFilter.id === FilterId.ROLE_TAXONOMY &&
          !roleTaxonomyValueChanged
        ) {
          return nextState;
        }

        if (
          updatedFilter.id === FilterId.SKILL_TAXONOMY &&
          !skillTaxonomyValueChanged
        ) {
          return nextState;
        }

        return processFilterDependencies(
          nextState,
          updatedFilter.id,
          updatedFilter
        );
      }

      return nextState;
    }

    case 'REMOVE_FILTER': {
      const defaultFilter = getFilterDefinition(action.filterId);
      if (!defaultFilter) {
        return state;
      }

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        action.filterId,
        defaultFilter,
        true
      );

      if (updatedFilter) {
        return processFilterDependencies(
          nextState,
          action.filterId,
          updatedFilter
        );
      }

      return nextState;
    }

    case 'REPLACE_FILTERS': {
      return handleReplaceFilters(state, action.payload);
    }

    case 'ADD_NEW_FILTER_GROUP': {
      return addNewFilterGroup({
        filterState: state,
        newFilterKey: action.payload.newFilterKey,
        newFilterMap: action.payload.newFilterMap,
      });
    }

    case 'TOGGLE_PRIMARY_SELECTION_ACTIVE': {
      const { limit, selectionId } = action.payload;
      return updatePrimaryFilterState(
        state,
        action.payload,
        (currentPageSelections) => {
          const target = currentPageSelections.find(
            (s) => s.id === selectionId
          );
          if (!target) return null;

          if (target.isActive) {
            // Deactivating: ensure at least one remains active
            const activeCount = currentPageSelections.filter(
              (s) => s.isActive
            ).length;
            return activeCount <= 1
              ? null // Don't allow deactivating the last one
              : currentPageSelections.map((s) =>
                  s.id === selectionId ? { ...s, isActive: false } : s
                );
          } else {
            // Activating: enforce the limit
            return activateSelection(currentPageSelections, selectionId, limit);
          }
        }
      );
    }

    case 'REMOVE_PRIMARY_SELECTION': {
      const { selectionId } = action.payload;
      return updatePrimaryFilterState(
        state,
        action.payload,
        (currentPageSelections) => {
          const { canRemove, updatedSelections } = removeSelection(
            currentPageSelections,
            selectionId
          );
          // Only update if removal is allowed
          return canRemove ? updatedSelections : null;
        }
      );
    }

    case 'REORDER_PRIMARY_SELECTIONS': {
      const { newPrimarySelections } = action.payload;
      return updatePrimaryFilterState(
        state,
        action.payload,
        () => newPrimarySelections
      );
    }

    case 'UPDATE_PRIMARY_SELECTIONS': {
      const { newSelections, limit } = action.payload;
      return updatePrimaryFilterState(
        state,
        action.payload,
        (currentPageSelections) => {
          return updatePrimarySelections(
            newSelections,
            currentPageSelections,
            limit
          );
        }
      );
    }

    case 'UPDATE_SEGMENT_IN_FILTER': {
      const { filterId, segmentKey, segmentFilter } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      if (!existingFilter || !isSegmentedFilter(existingFilter)) {
        return state;
      }

      const updatedSegmentations = {
        ...(existingFilter.segmentations || {}),
        [segmentKey]: segmentFilter,
      };

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: existingFilter.wrappedFilter,
        baseProps: existingFilter,
        segmentations: updatedSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    case 'REMOVE_SEGMENT_FROM_FILTER': {
      const { filterId, segmentKey } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      if (!existingFilter || !isSegmentedFilter(existingFilter)) {
        return state;
      }

      const existingSegmentations = existingFilter.segmentations || {};
      const updatedSegmentations = Object.fromEntries(
        Object.entries(existingSegmentations).filter(
          ([key]) => key !== segmentKey
        )
      );

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: existingFilter.wrappedFilter,
        baseProps: existingFilter,
        segmentations: updatedSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    case 'UPDATE_SEGMENTED_FILTER': {
      const { filterId, wrappedFilter, segmentations } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      // Ensure wrappedFilter is a segmentable type
      if (
        wrappedFilter.type !== FilterType.TREE &&
        wrappedFilter.type !== FilterType.LIST &&
        wrappedFilter.type !== FilterType.STATIC_LIST &&
        wrappedFilter.type !== FilterType.RANGE
      ) {
        return state;
      }

      // Type-safe creation/update of SegmentedFilter based on the wrapped filter type
      const baseProps =
        existingFilter && isSegmentedFilter(existingFilter)
          ? existingFilter // Preserve BaseFilter properties (like isUserSubmitted)
          : wrappedFilter; // New filter - wrappedFilter already has all BaseFilter properties

      const existingSegmentations =
        existingFilter && isSegmentedFilter(existingFilter)
          ? existingFilter.segmentations
          : undefined;
      const finalSegmentations = segmentations ?? existingSegmentations ?? {};

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: wrappedFilter as
          | TreeFilter
          | ListFilter
          | StaticListFilter
          | RangeFilter,
        baseProps,
        segmentations: finalSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    default:
      return state;
  }
};
